import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, Filter, Smartphone, Cpu, Shield, Gamepad2, Server, BarChart3, Globe, Send, Heart, Briefcase, User } from 'lucide-react';
import { projects } from '../data/mockData';
import { useTheme } from '../contexts/ThemeContext';
import { trackButtonClick } from '../utils/trackButtonClick';

const ProjectsSection = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [hoveredProject, setHoveredProject] = useState(null);
  const { isDark, background, text, accents } = useTheme();
  const controls = useAnimation();
  const [sectionRef, inView] = useInView({
    threshold: 0,
    triggerOnce: true
  });

  const categories = ['All', ...new Set(projects.map(project => project.category))];
  
  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  useEffect(() => {
    if (inView) {
      controls.start('visible');
      return;
    }
    // Safety net: if the scroll-triggered observer never fires (can happen
    // on mobile with fast flick scrolling past a viewport-height section),
    // reveal the section anyway after a short delay instead of staying blank.
    const fallback = setTimeout(() => controls.start('visible'), 1500);
    return () => clearTimeout(fallback);
  }, [controls, inView]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.9
    },
    visible: {
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const filterVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const projectCardVariants = {
    hidden: { 
      opacity: 0, 
      y: 50,
      scale: 0.95
    },
    visible: {
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const categoryIcons = {
    'Enterprise': Server,
    'AI/ML': Cpu,
    'System Tools': BarChart3,
    'Gaming': Gamepad2,
    'Security': Shield,
    'DevOps': Server,
    'Health & Wellness': Heart
  };

  return (
    <motion.section 
      id="projects" 
      ref={sectionRef}
      className={`min-h-screen py-20 ${background.section} relative overflow-hidden transition-all duration-500`}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
    >
      {/* Dynamic Background Based on Hovered Project */}
      <motion.div 
        className="absolute inset-0 transition-all duration-1000 ease-out"
        animate={{
          background: hoveredProject ? (
            hoveredProject.category === 'Enterprise' ? (isDark ? 'linear-gradient(to bottom right, rgba(30, 64, 175, 0.1))' : 'linear-gradient(to bottom right, rgba(219, 234, 254, 0.3))') :
            hoveredProject.category === 'AI/ML' ? (isDark ? 'linear-gradient(to bottom right, rgba(88, 28, 135, 0.1))' : 'linear-gradient(to bottom right, rgba(243, 232, 255, 0.3))') :
            hoveredProject.category === 'Security' ? (isDark ? 'linear-gradient(to bottom right, rgba(127, 29, 29, 0.1))' : 'linear-gradient(to bottom right, rgba(254, 226, 226, 0.3))') :
            isDark ? 'linear-gradient(to bottom right, rgba(21, 128, 61, 0.1))' : 'linear-gradient(to bottom right, rgba(220, 252, 231, 0.3))'
          ) : 'transparent'
        }}
        transition={{ duration: 1 }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div 
            className="text-center mb-16"
            variants={itemVariants}
          >
            <motion.div 
              className={`inline-block px-4 py-2 ${background.card} rounded-full ${accents.projects} font-mono text-sm mb-6`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              &gt; projects --showcase
            </motion.div>
            <motion.h2 
              className={`text-5xl lg:text-7xl font-bold ${text.gradient} mb-6`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Projects
            </motion.h2>
            <motion.p 
              className={`text-xl ${text.secondary} max-w-3xl mx-auto`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Innovative iOS, macOS, and enterprise applications showcasing cutting-edge technology and exceptional user experiences
            </motion.p>
          </motion.div>

          {/* Filter Buttons */}
          <motion.div 
            className="flex flex-wrap justify-center gap-4 mb-16"
            variants={containerVariants}
          >
            {categories.map((category, index) => {
              const Icon = categoryIcons[category] || Filter;
              return (
                <motion.button
                  key={category}
                  onClick={() => {
                    trackButtonClick({ section: 'projects', label: category, type: 'filter' });
                    setActiveFilter(category);
                  }}
                  className={`group flex items-center space-x-2 px-6 py-3 rounded-2xl font-medium transition-all duration-300 ${
                    activeFilter === category
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/25 scale-105'
                      : isDark
                        ? 'bg-white/5 backdrop-blur border border-white/10 text-gray-300 hover:text-white hover:bg-white/10 hover:border-white/20'
                        : 'bg-white border border-gray-200 text-gray-700 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300 shadow-sm'
                  }`}
                  variants={filterVariants}
                  custom={index}
                  whileHover={{ 
                    scale: activeFilter === category ? 1.05 : 1.02,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                >
                  <Icon className="w-4 h-4" />
                  <span>{category}</span>
                </motion.button>
              );
            })}
          </motion.div>

          {/* Projects Grid */}
          <motion.div 
            className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-16"
            variants={containerVariants}
            key={activeFilter} // Re-trigger animation when filter changes
            initial="hidden"
            animate="visible"
          >
            {filteredProjects.map((project, index) => {
              const CategoryIcon = categoryIcons[project.category] || Smartphone;
              
              return (
                <motion.div
                  key={`${activeFilter}-${project.id}`}
                  className="group relative"
                  variants={projectCardVariants}
                  whileHover="hover"
                  whileTap={{ scale: 0.98 }}
                  onHoverStart={() => setHoveredProject(project)}
                  onHoverEnd={() => setHoveredProject(null)}
                  initial="hidden"
                  animate="visible"
                  transition={{ 
                    duration: 0.6,
                    delay: index * 0.1,
                    ease: "easeOut"
                  }}
                >
                  <motion.div 
                    className={`relative ${background.card} rounded-2xl overflow-hidden shadow-2xl hover:shadow-cyan-500/10 transition-all duration-500 group`}
                    whileHover={{ 
                      boxShadow: isDark 
                        ? "0 25px 50px -12px rgba(6, 182, 212, 0.25)" 
                        : "0 25px 50px -12px rgba(6, 182, 212, 0.15)"
                    }}
                  >
                    {/* Project Image with Overlay */}
                    <div className={`relative h-64 overflow-hidden ${project.isAppIcon ? (isDark ? 'bg-gradient-to-br from-slate-800 to-slate-900' : 'bg-gradient-to-br from-slate-100 to-slate-200') : ''}`}>
                      {/* Personal/Professional Strip at Top */}
                      <div 
                        className={`absolute top-0 left-0 right-0 z-20 px-3 py-1 text-xs font-semibold flex items-center justify-center gap-1.5 ${
                          project.isPersonal
                            ? (isDark 
                                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white' 
                                : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white')
                            : (isDark 
                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white' 
                                : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white')
                        }`}
                      >
                        {project.isPersonal ? (
                          <>
                            <User className="w-3 h-3" />
                            <span>Personal</span>
                          </>
                        ) : (
                          <>
                            <Briefcase className="w-3 h-3" />
                            <span>Professional</span>
                          </>
                        )}
                      </div>

                      <motion.img
                        src={project.image}
                        alt={project.title}
                        className={`w-full h-full ${project.isAppIcon ? 'object-contain p-6' : 'object-cover'}`}
                        whileHover={{ scale: 1.08 }}
                        transition={{ duration: 0.6 }}
                      />
                      
                      {/* Gradient Overlay - always dark so title text stays legible regardless of image brightness or theme */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent group-hover:from-black/95 group-hover:via-black/50 transition-all duration-300"></div>

                      {/* Category Badge */}
                      <motion.div 
                        className={`absolute top-10 left-4 flex items-center space-x-2 px-3 py-1 ${isDark ? 'bg-black/50' : 'bg-white/80'} backdrop-blur border ${isDark ? 'border-white/20' : 'border-gray-200'} rounded-full`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <CategoryIcon className={`w-4 h-4 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                        <span className={`${isDark ? 'text-cyan-400' : 'text-cyan-700 font-semibold'} text-sm font-medium`}>{project.category}</span>
                      </motion.div>
                      
                      {/* Enterprise Badge */}
                      {project.isEnterprise && (
                        <motion.div 
                          className={`absolute top-10 right-4 px-3 py-2 ${isDark ? 'bg-gradient-to-r from-yellow-500/80 to-amber-600/80 border-yellow-300 shadow-lg shadow-yellow-500/30' : 'bg-gradient-to-r from-yellow-400 to-amber-500 border-yellow-600 shadow-lg shadow-yellow-400/40'} backdrop-blur-sm border-2 rounded-lg text-xs font-bold ${isDark ? 'text-yellow-100' : 'text-white'}`}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ 
                            delay: 0.5,
                            type: "spring",
                            stiffness: 200
                          }}
                          whileHover={{ 
                            scale: 1.1,
                            rotate: 2
                          }}
                        >
                          ENTERPRISE
                        </motion.div>
                      )}

                      {/* Project Title Overlay */}
                      <div className="absolute bottom-4 left-4 right-4">
                        <motion.h3
                          className="text-xl font-extrabold text-white mb-2 tracking-tight"
                          style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9), 0 1px 12px rgba(0,0,0,0.6)' }}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          {project.title}
                        </motion.h3>
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="p-6">
                      <p className={`${text.secondary} text-sm leading-relaxed mb-4 group-hover:${text.primary} transition-colors`}>
                        {project.description}
                      </p>

                      {/* Platform Information */}
                      {project.platform && (
                        <div className="mb-4">
                          <div className="flex items-center space-x-2">
                            <Smartphone className="w-4 h-4 text-purple-400" />
                            <span className={`${text.primary} text-sm font-medium`}>Platform:</span>
                            <span className={`${text.secondary} text-sm`}>{project.platform}</span>
                          </div>
                        </div>
                      )}

                      {/* Technologies */}
                      <div className="mb-4 max-w-full">
                        <div className="flex flex-wrap gap-2 items-start" style={{maxHeight: '4.5rem', overflow: 'hidden'}}>
                          {project.technologies.slice(0, 3).map((tech, techIndex) => (
                            <span
                              key={techIndex}
                              title={tech}
                              className={`px-2 py-1 ${isDark ? 'bg-white/10 border-white/20 text-gray-300 hover:bg-white/20 hover:text-white' : 'bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200 hover:text-gray-900'} text-xs rounded-lg font-medium border transition-all duration-300 flex-shrink-0 leading-tight max-w-[140px]`}
                              style={{ 
                                wordBreak: 'break-word',
                                lineHeight: '1.2',
                                whiteSpace: 'normal',
                                height: 'auto'
                              }}
                            >
                              {tech.length > 18 ? `${tech.substring(0, 16)}...` : tech}
                            </span>
                          ))}
                          {project.technologies.length > 3 && (
                            <span 
                              title={`Additional technologies: ${project.technologies.slice(3).join(', ')}`}
                              className={`px-2 py-1 ${isDark ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-300' : 'bg-gradient-to-r from-cyan-600 to-blue-600 border border-cyan-700 text-white'} text-xs rounded-lg font-medium flex-shrink-0 leading-tight`}
                              style={{ lineHeight: '1.2' }}
                            >
                              +{project.technologies.length - 3}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Key Features */}
                      <div className="space-y-2 mb-6">
                        {project.features.slice(0, 4).map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-start space-x-2">
                            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                            <span className={`${text.muted} text-xs leading-relaxed group-hover:${text.secondary} transition-colors`}>
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      {project.links ? (
                        <div className="flex space-x-2">
                          {project.links.ios && (
                            <a
                              href={project.links.ios}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackButtonClick({ section: 'projects', label: 'iOS', type: 'project_link', detail: project.title })}
                              className={`flex-1 ${isDark ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 hover:from-cyan-500/30 hover:to-blue-500/30 border border-cyan-500/30 hover:border-cyan-400 text-cyan-300' : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 border border-cyan-700 hover:border-cyan-800 text-white'} hover:${text.primary} px-3 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2 text-sm`}
                            >
                              <Smartphone className="w-4 h-4" />
                              <span>iOS</span>
                            </a>
                          )}
                          {project.links.android && (
                            <a
                              href={project.links.android}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackButtonClick({ section: 'projects', label: 'Android', type: 'project_link', detail: project.title })}
                              className={`flex-1 ${isDark ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30 border border-green-500/30 hover:border-green-400 text-green-300' : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 border border-green-700 hover:border-green-800 text-white'} hover:${text.primary} px-3 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2 text-sm`}
                            >
                              <ExternalLink className="w-4 h-4" />
                              <span>Android</span>
                            </a>
                          )}
                          {project.links.web && (
                            <a
                              href={project.links.web}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackButtonClick({ section: 'projects', label: 'Website', type: 'project_link', detail: project.title })}
                              className={`${project.links.telegram ? 'flex-1' : 'w-full'} ${isDark ? 'bg-gradient-to-r from-purple-500/20 to-violet-500/20 hover:from-purple-500/30 hover:to-violet-500/30 border border-purple-500/30 hover:border-purple-400 text-purple-300' : 'bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 border border-purple-700 hover:border-purple-800 text-white'} hover:${text.primary} px-4 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2`}
                            >
                              <Globe className="w-4 h-4" />
                              <span>Website</span>
                            </a>
                          )}
                          {project.links.telegram && (
                            <a
                              href={project.links.telegram}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackButtonClick({ section: 'projects', label: 'Telegram', type: 'project_link', detail: project.title })}
                              className={`flex-1 ${isDark ? 'bg-gradient-to-r from-sky-500/20 to-blue-500/20 hover:from-sky-500/30 hover:to-blue-500/30 border border-sky-500/30 hover:border-sky-400 text-sky-300' : 'bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 border border-sky-600 hover:border-sky-700 text-white'} hover:${text.primary} px-4 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2`}
                            >
                              <Send className="w-4 h-4" />
                              <span>Telegram</span>
                            </a>
                          )}
                        </div>
                      ) : project.title !== "CRM Tool (Internal)" && project.title !== "Nutrigrain Sports Trainer" ? (
                        <button className={`w-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 hover:from-cyan-500/30 hover:to-blue-500/30 border border-cyan-500/30 hover:border-cyan-400 text-cyan-300 hover:${text.primary} px-4 py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2`}>
                          <ExternalLink className="w-4 h-4" />
                          <span>Explore Project</span>
                        </button>
                      ) : null}
                    </div>

                    {/* Hover Glow Effect */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/0 via-blue-500/0 to-purple-500/0 group-hover:from-cyan-500/10 group-hover:via-blue-500/5 group-hover:to-purple-500/10 transition-all duration-700 pointer-events-none"></div>
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Call to Action */}
          <div className="text-center">
            <div className={`inline-block p-8 ${background.card} rounded-2xl shadow-2xl`}>
              <h3 className={`text-2xl font-bold ${text.primary} mb-4`}>
                Ready to Build Something Amazing?
              </h3>
              <p className={`${text.secondary} mb-6 max-w-md`}>
                Let's collaborate on your next innovative iOS project and create exceptional user experiences together.
              </p>
              <button
                onClick={() => {
                  trackButtonClick({ section: 'projects', label: 'Start a Project', type: 'cta' });
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white px-8 py-4 rounded-2xl font-semibold shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 transition-all duration-300 hover:scale-105 flex items-center space-x-2 mx-auto"
              >
                <span>Start a Project</span>
                <ExternalLink className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-slideUp {
          animation: slideUp 0.8s ease-out forwards;
        }
      `}</style>
    </motion.section>
  );
};

export default ProjectsSection;